import { useState, useCallback } from 'react';
import { App } from 'antd';
import CSAPdfGenerator from '../services/csaPdfGenerator';
import { csaService } from '../services/dashboard/csa.service';
import { DashboardCSAData, VulnerabilidadeCSA, FiltrosVulnerabilidades } from '../types/dashboard/csa.types';
import { RelatorioOptions } from '../components/dashboards/Cybersecurity/CSARelatorioModal';

interface UseCsaRelatorioReturn {
 gerarRelatorio: (
   clienteId: string, 
   options: RelatorioOptions, 
   dashboardData?: DashboardCSAData
 ) => Promise<boolean>;
 loading: boolean;
 error: string | null;
 progresso: number;
 ultimoRelatorio: {
   fileName?: string;
   dataGeracao?: Date;
   tamanho?: number;
   pdfUrl?: string;
 } | null;
}

interface UseCsaRelatorioExtendedReturn extends UseCsaRelatorioReturn {
 validarOpcoes: (options: RelatorioOptions) => string[];
 limparError: () => void;
 obterUltimoRelatorio: () => UseCsaRelatorioReturn['ultimoRelatorio'];
 calcularEstatisticasRelatorio: (
   vulnerabilidades: VulnerabilidadeCSA[],
   options: RelatorioOptions
 ) => {
   totalVulnerabilidades: number;
   vulnerabilidadesPorSeveridade: Record<string, number>;
   hostsMaisAfetados: Array<{ host: string; count: number }>;
   periodo: {
     inicio: string;
     fim: string;
     dias: number;
   };
 };
}

export const useCsaRelatorio = (): UseCsaRelatorioReturn => {
 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [progresso, setProgresso] = useState(0);
 const [ultimoRelatorio, setUltimoRelatorio] = useState<UseCsaRelatorioReturn['ultimoRelatorio']>(null);

 const { message } = App.useApp();

 const atualizarProgresso = useCallback((valor: number, mensagem?: string) => {
   setProgresso(valor);
   if (mensagem) {
     message.loading(mensagem, 0);
   }
 }, [message]);

 const normalizarCVE = useCallback((cve: string): string => {
   if (!cve || cve.trim() === '' || cve.toLowerCase() === 'null') {
     return 'CVE-PENDENTE';
   }
   return cve.trim();
 }, []);

 const normalizarHost = useCallback((host: string, ip: string): string => {
   if (!host || host.trim() === '' || host.toLowerCase() === 'unknown') {
     return ip ? `IP-${ip}` : 'IP-Desconhecido';
   }
   return host.trim();
 }, []);

 const priorizarVulnerabilidades = useCallback((vulnerabilidades: VulnerabilidadeCSA[]): VulnerabilidadeCSA[] => {
   return vulnerabilidades.sort((a, b) => {
     const scoreA = parseFloat(a.cvssScore) || 0;
     const scoreB = parseFloat(b.cvssScore) || 0;
     if (scoreA !== scoreB) {
       return scoreB - scoreA;
     }
     
     const severityOrder = { 'high': 4, 'medium': 3, 'low': 2, 'info': 1 };
     const severityA = severityOrder[a.severity?.toLowerCase() as keyof typeof severityOrder] || 0;
     const severityB = severityOrder[b.severity?.toLowerCase() as keyof typeof severityOrder] || 0;
     if (severityA !== severityB) {
       return severityB - severityA;
     }
     
     const dateA = new Date(a.created_at).getTime();
     const dateB = new Date(b.created_at).getTime();
     return dateB - dateA;
   });
 }, []);

 const filtrarPorPeriodo = useCallback((vulnerabilidades: VulnerabilidadeCSA[], periodo: RelatorioOptions['periodo']): VulnerabilidadeCSA[] => {
   return vulnerabilidades.filter(vuln => {
     try {
       const dataVuln = new Date(vuln.created_at);
       
       const dataInicio = new Date(periodo.inicio);
       const dataFim = new Date(periodo.fim);
       
       dataInicio.setHours(0, 0, 0, 0);
       dataFim.setHours(23, 59, 59, 999);
       
       const dataVulnNormalizada = new Date(dataVuln);
       dataVulnNormalizada.setHours(12, 0, 0, 0);
       
       if (isNaN(dataVuln.getTime()) || isNaN(dataInicio.getTime()) || isNaN(dataFim.getTime())) {
         return false;
       }
       
       return dataVulnNormalizada >= dataInicio && dataVulnNormalizada <= dataFim;
     } catch (error) {
       return false;
     }
   });
 }, []);

 const filtrarPorSeveridade = useCallback((vulnerabilidades: VulnerabilidadeCSA[], severidades: string[]): VulnerabilidadeCSA[] => {
   if (!severidades || severidades.length === 0 || severidades.length === 4) {
     return vulnerabilidades;
   }
   
   return vulnerabilidades.filter(vuln => {
     const severidadeVuln = vuln.severity?.toLowerCase() || 'info';
     return severidades.includes(severidadeVuln);
   });
 }, []);

 const filtrarPorCVSS = useCallback((vulnerabilidades: VulnerabilidadeCSA[], cvssMinimo: number): VulnerabilidadeCSA[] => {
   return vulnerabilidades.filter(vuln => {
     const score = parseFloat(vuln.cvssScore);
     return !isNaN(score) && score >= cvssMinimo;
   });
 }, []);

 const filtrarPorHost = useCallback((vulnerabilidades: VulnerabilidadeCSA[], hostFiltro: string): VulnerabilidadeCSA[] => {
   const hostLower = hostFiltro.toLowerCase();
   
   return vulnerabilidades.filter(vuln => {
     const hostNormalizado = normalizarHost(vuln.host, vuln.ip).toLowerCase();
     const hostOriginal = (vuln.host || '').toLowerCase();
     const ip = (vuln.ip || '').toLowerCase();
     
     return hostNormalizado.includes(hostLower) || 
            hostOriginal.includes(hostLower) || 
            ip.includes(hostLower);
   });
 }, [normalizarHost]);

 const filtrarPorCVE = useCallback((vulnerabilidades: VulnerabilidadeCSA[], cveFiltro: string): VulnerabilidadeCSA[] => {
   const cveLower = cveFiltro.toLowerCase();
   
   return vulnerabilidades.filter(vuln => {
     const cveNormalizado = normalizarCVE(vuln.cve).toLowerCase();
     const cveOriginal = (vuln.cve || '').toLowerCase();
     
     if (cveLower.includes('pendente') || cveLower.includes('pending') || cveLower === 'null') {
       return cveNormalizado.includes('pendente') || 
              cveOriginal === 'null' || 
              cveOriginal === '' || 
              !vuln.cve;
     }
     
     return cveNormalizado.includes(cveLower) || cveOriginal.includes(cveLower);
   });
 }, [normalizarCVE]);

 const filtrarPorPorta = useCallback((vulnerabilidades: VulnerabilidadeCSA[], portaFiltro: string): VulnerabilidadeCSA[] => {
   return vulnerabilidades.filter(vuln => {
     const porta = vuln.port || '';
     return porta.includes(portaFiltro);
   });
 }, []);

 const aplicarFiltros = useCallback((vulnerabilidades: VulnerabilidadeCSA[], options: RelatorioOptions): VulnerabilidadeCSA[] => {
   let resultado = [...vulnerabilidades];
   
   resultado = filtrarPorPeriodo(resultado, options.periodo);
   resultado = filtrarPorSeveridade(resultado, options.severidades);
   
   if (options.filtros.cvssMinimo !== undefined && options.filtros.cvssMinimo !== null) {
     resultado = filtrarPorCVSS(resultado, options.filtros.cvssMinimo);
   }
   
   if (options.filtros.host && options.filtros.host.trim() !== '') {
     resultado = filtrarPorHost(resultado, options.filtros.host.trim());
   }
   
   if (options.filtros.cveEspecifico && options.filtros.cveEspecifico.trim() !== '') {
     resultado = filtrarPorCVE(resultado, options.filtros.cveEspecifico.trim());
   }
   
   if (options.filtros.portaEspecifica && options.filtros.portaEspecifica.trim() !== '') {
     resultado = filtrarPorPorta(resultado, options.filtros.portaEspecifica.trim());
   }
   
   return resultado;
 }, [filtrarPorPeriodo, filtrarPorSeveridade, filtrarPorCVSS, filtrarPorHost, filtrarPorCVE, filtrarPorPorta]);

 const gerarRelatorio = useCallback(async (
   clienteId: string,
   options: RelatorioOptions,
   dashboardData?: DashboardCSAData
 ): Promise<boolean> => {
   if (!clienteId) {
     setError('ID do cliente é obrigatório');
     return false;
   }

   setLoading(true);
   setError(null);
   setProgresso(0);

   try {
     atualizarProgresso(10, 'Coletando dados do dashboard...');
     
     let dadosDashboard = dashboardData;
     if (!dadosDashboard) {
       try {
         dadosDashboard = await csaService.getDashboardStats(clienteId);
       } catch (err) {
         throw new Error('Erro ao carregar dados do dashboard: ' + (err as Error).message);
       }
     }

     atualizarProgresso(25, 'Buscando vulnerabilidades...');
     
     let todasVulnerabilidades: VulnerabilidadeCSA[] = [];
     let totalBuscado = 0;
     let pagina = 1;
     const limitePorPagina = 100;

     while (true) {
       try {
         const filtrosVulnerabilidades: FiltrosVulnerabilidades = {
           page: pagina,
           limit: limitePorPagina,
           sortBy: 'created_at',
           sortOrder: 'desc'
         };

         const responseVulnerabilidades = await csaService.getVulnerabilidades(
           clienteId, 
           filtrosVulnerabilidades
         );
         
         const vulnerabilidadesPagina = responseVulnerabilidades.vulnerabilidades;
         todasVulnerabilidades.push(...vulnerabilidadesPagina);
         totalBuscado += vulnerabilidadesPagina.length;
         
         if (vulnerabilidadesPagina.length < limitePorPagina) {
           break;
         }
         
         pagina++;
         
         const progressoBusca = Math.min(25 + (pagina * 5), 35);
         atualizarProgresso(progressoBusca, `Buscando vulnerabilidades... (${totalBuscado} encontradas)`);
         
       } catch (err) {
         break;
       }
     }

     atualizarProgresso(40, 'Aplicando filtros...');
     
     const vulnerabilidadesFinais = aplicarFiltros(todasVulnerabilidades, options);

     atualizarProgresso(60, 'Validando dados...');
     
     if (!dadosDashboard) {
       throw new Error('Dados do dashboard não disponíveis');
     }

     const vulnerabilidadesNormalizadas = vulnerabilidadesFinais.map(vuln => ({
       ...vuln,
       cve: normalizarCVE(vuln.cve),
       host: normalizarHost(vuln.host, vuln.ip)
     }));

     atualizarProgresso(65, 'Priorizando vulnerabilidades...');
     
     const vulnerabilidadesPriorizadas = priorizarVulnerabilidades(vulnerabilidadesNormalizadas);

     atualizarProgresso(70, 'Gerando relatório PDF...');
     
     const pdfGenerator = new CSAPdfGenerator();
     const resultado = await pdfGenerator.gerarRelatorio(
       dadosDashboard.cliente.nome,
       dadosDashboard,
       vulnerabilidadesPriorizadas,
       options
     );

     if (!resultado.success) {
       throw new Error(resultado.error || 'Erro desconhecido ao gerar PDF');
     }

     atualizarProgresso(100, 'Relatório gerado com sucesso!');
     
     setUltimoRelatorio({
       fileName: resultado.fileName,
       dataGeracao: new Date(),
       pdfUrl: resultado.pdfUrl,
       tamanho: undefined
     });

     setTimeout(() => {
       message.destroy();
     }, 2000);

     return true;

   } catch (err) {
     const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido ao gerar relatório';
     setError(errorMessage);
     message.destroy();
     message.error(errorMessage);
     return false;
   } finally {
     setLoading(false);
     setProgresso(0);
   }
 }, [atualizarProgresso, aplicarFiltros, normalizarCVE, normalizarHost, priorizarVulnerabilidades, message]);

 return {
   gerarRelatorio,
   loading,
   error,
   progresso,
   ultimoRelatorio
 };
};

export const useCsaRelatorioExtended = (): UseCsaRelatorioExtendedReturn => {
 const baseHook = useCsaRelatorio();

 const validarOpcoes = useCallback((options: RelatorioOptions): string[] => {
   const erros: string[] = [];

   if (!options.periodo.inicio || !options.periodo.fim) {
     erros.push('Período de análise é obrigatório');
   } else {
     const inicio = new Date(options.periodo.inicio);
     const fim = new Date(options.periodo.fim);
     
     if (inicio > fim) {
       erros.push('Data de início deve ser anterior à data de fim');
     }
     
     if (fim > new Date()) {
       erros.push('Data de fim não pode ser futura');
     }
   }

   if (!options.severidades || options.severidades.length === 0) {
     erros.push('Pelo menos uma severidade deve ser selecionada');
   }

   const temConteudo = options.incluirDetalhes || 
                      options.incluirGraficos || 
                      options.incluirRecomendacoes || 
                      options.incluirResumoExecutivo;
   
   if (!temConteudo) {
     erros.push('Pelo menos um tipo de conteúdo deve ser incluído no relatório');
   }

   return erros;
 }, []);

 const limparError = useCallback(() => {
   // Functionality not implemented in base hook
 }, []);

 const obterUltimoRelatorio = useCallback(() => {
   return baseHook.ultimoRelatorio;
 }, [baseHook.ultimoRelatorio]);

 const calcularEstatisticasRelatorio = useCallback((
   vulnerabilidades: VulnerabilidadeCSA[],
   options: RelatorioOptions
 ) => {
   const stats = {
     totalVulnerabilidades: vulnerabilidades.length,
     vulnerabilidadesPorSeveridade: {} as Record<string, number>,
     hostsMaisAfetados: [] as Array<{ host: string; count: number }>,
     periodo: {
       inicio: options.periodo.inicio,
       fim: options.periodo.fim,
       dias: Math.ceil(
         (new Date(options.periodo.fim).getTime() - new Date(options.periodo.inicio).getTime()) / (1000 * 60 * 60 * 24)
       )
     }
   };

   vulnerabilidades.forEach(vuln => {
     const severity = vuln.severity || 'unknown';
     stats.vulnerabilidadesPorSeveridade[severity] = 
       (stats.vulnerabilidadesPorSeveridade[severity] || 0) + 1;
   });

   const hostCount: Record<string, number> = {};
   vulnerabilidades.forEach(vuln => {
     const host = vuln.host || 'unknown';
     hostCount[host] = (hostCount[host] || 0) + 1;
   });

   stats.hostsMaisAfetados = Object.entries(hostCount)
     .map(([host, count]) => ({ host, count }))
     .sort((a, b) => b.count - a.count)
     .slice(0, 10);

   return stats;
 }, []);

 return {
   ...baseHook,
   validarOpcoes,
   limparError,
   obterUltimoRelatorio,
   calcularEstatisticasRelatorio
 };
};